import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Save, Building2, Folder } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useS3Upload from '../hooks/useS3Upload';
import S3AssetBrowser from '../components/S3AssetBrowser';

const FacilityPhotoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // For editing existing facility photo
  const { token } = useAuth();
  const { uploadFile, uploading, uploadProgress } = useS3Upload();
  
  const [saving, setSaving] = useState(false);
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [error, setError] = useState('');
  const [showAssetBrowser, setShowAssetBrowser] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'interior',
    order: 0,
    image: null,
    imageUrl: '', // S3 URL
    s3Key: '' // S3 key for deletion
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const isEditing = !!id;

  // Load existing facility data if editing
  useEffect(() => {
    if (isEditing) {
      fetchFacilityData();
    }
  }, [id]);

  const fetchFacilityData = async () => {
    try {
      // Get all facility photos and find the one we're editing
      const response = await fetch(`${API_BASE_URL}/api/facility-photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const facilityPhotos = data.facility_photos || [];
        const currentPhoto = facilityPhotos.find(photo => photo.id === id);
        
        if (currentPhoto) {
          setFormData({
            title: currentPhoto.title || '',
            description: currentPhoto.description || '',
            category: currentPhoto.category || 'interior',
            order: currentPhoto.order || 0,
            image: null, // Don't set existing image in file input
            imageUrl: currentPhoto.photo_url || '',
            s3Key: currentPhoto.s3_key || ''
          });
        } else {
          setError('Facility photo not found');
        }
      } else {
        setError('Failed to load facility photo data');
      }
    } catch (error) {
      console.error('Error fetching facility data:', error);
      setError('Failed to load facility data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        // Upload to S3 immediately
        const result = await uploadFile(file, { folderType: 'images' });
        
        setFormData(prev => ({
          ...prev,
          image: file,
          imageUrl: result.file_url,
          s3Key: result.s3_key
        }));
      } catch (error) {
        setError(`Upload failed: ${error.message}`);
      }
    }
  };

  const handleAssetSelect = (asset) => {
    setFormData(prev => ({
      ...prev,
      image: null, // No file object for existing assets
      imageUrl: asset.url,
      s3Key: asset.key
    }));
    setShowAssetBrowser(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!isEditing && !formData.imageUrl) {
      setError('Please select and upload an image or browse existing assets');
      return;
    }

    setSaving(true);
    setShowSavingModal(true);
    setError('');

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        order: parseInt(formData.order),
        photo_url: formData.imageUrl, // S3 URL
        s3_key: formData.s3Key // For deletion reference
      };

      const url = isEditing 
        ? `${API_BASE_URL}/api/facility-photos/${id}`
        : `${API_BASE_URL}/api/facility-photos`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Success - wait a moment then redirect back
        setTimeout(() => {
          setShowSavingModal(false);
          navigate('/photo-management?tab=facility');
        }, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save facility photo');
      }
    } catch (error) {
      console.error('Error saving facility photo:', error);
      setError(error.message);
      setShowSavingModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/photo-management?tab=facility');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Facility Photo' : 'Add New Facility Photo'}
              </h1>
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="Reception Area"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="Our welcoming reception area where clients and pets are greeted"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                >
                  <option value="interior">Interior</option>
                  <option value="exterior">Exterior</option>
                  <option value="equipment">Equipment</option>
                  <option value="treatment">Treatment Rooms</option>
                  <option value="waiting">Waiting Areas</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  placeholder="0"
                  min="0"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isEditing ? 'Replace Image (optional)' : 'Image *'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                  {uploading ? (
                    <div className="space-y-4">
                      <Upload className="h-12 w-12 mx-auto text-theme-blue animate-pulse" />
                      <div>
                        <p className="text-sm text-theme-blue mb-2">Uploading to AWS S3...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-theme-blue h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
                      </div>
                    </div>
                  ) : formData.imageUrl ? (
                    <div className="space-y-4">
                      <div className="relative">
                        <img 
                          src={formData.imageUrl} 
                          alt="Selected" 
                          className="max-h-48 mx-auto rounded-lg border border-gray-200"
                        />
                        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-green-600">✅ Image ready for upload</p>
                      <div className="flex items-center justify-center space-x-3">
                        <label className="inline-flex items-center px-3 py-2 text-sm text-theme-blue border border-blue-300 rounded-md hover:bg-blue-50 transition-colors cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" />
                          Replace Image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowAssetBrowser(true)}
                          className="inline-flex items-center px-3 py-2 text-sm text-green-600 border border-green-300 rounded-md hover:bg-green-50 transition-colors"
                        >
                          <Folder className="h-4 w-4 mr-2" />
                          Browse Assets
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Building2 className="h-12 w-12 mx-auto text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          {isEditing ? 'Upload a new image or browse existing assets' : 'Upload facility photo or browse existing assets'}
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                          Recommended size: 800x600px for best results
                        </p>
                      </div>
                      <div className="flex items-center justify-center space-x-3">
                        <label className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors cursor-pointer" style={{ backgroundColor: 'var(--theme-primary)' }}>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload New
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowAssetBrowser(true)}
                          className="inline-flex items-center px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <Folder className="h-4 w-4 mr-2" />
                          Browse Assets
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {formData.image && (
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: {formData.image.name} ({(formData.image.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center px-6 py-2 text-white rounded-md hover:opacity-90 transition-colors disabled:bg-gray-400"
                  style={{ backgroundColor: saving ? '#9CA3AF' : 'var(--theme-primary)' }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : isEditing ? 'Update Facility Photo' : 'Create Facility Photo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Saving Modal */}
      {showSavingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Saving...
              </h3>
              <p className="text-gray-600">
                {isEditing ? 'Updating facility photo' : 'Creating facility photo'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* S3 Asset Browser Modal */}
      {showAssetBrowser && (
        <S3AssetBrowser
          isOpen={showAssetBrowser}
          onClose={() => setShowAssetBrowser(false)}
          onSelectAsset={handleAssetSelect}
          allowedTypes={['images']}
          title="Browse Facility Images"
          showUpload={true}
        />
      )}
    </div>
  );
};

export default FacilityPhotoForm;